/**
 * Aplica TemplateLibraryItem no `node.data.config` principal de um node do flow.
 *
 * Diferente do `templateToBlocks` (que constrói ContentBlock[] pra "blocks
 * adicionais"), aqui preenchemos os campos NATIVOS do config principal
 * (title, subtitle, media_url, buttons, message) — que são editados nos
 * Config components específicos (PhotoTemplateConfig, MessageConfig, etc).
 *
 * Mapeamento por nodeType:
 *   - photo_template / photo: title + subtitle + media_url + buttons (com imagem)
 *   - message_template / message: message (texto consolidado) + buttons
 *
 * Botões viram ButtonData com id negativo (sentinela "novo" — o backend
 * cria IDs reais no save).
 */

import type { NodeConfig, ButtonData } from '@/types/flow';
import type { TemplateLibraryItem } from '@/types/template-library';

export function applyTemplateToNodeConfig(
    template: TemplateLibraryItem,
    nodeType: string,
    currentConfig: NodeConfig | undefined,
): NodeConfig {
    const base: NodeConfig = { ...(currentConfig || {}) };
    const isCard = template.template_type === 'generic';
    const buttons: ButtonData[] = (template.buttons_json ?? [])
        .filter((b) => b && (b.title || b.url))
        .map<ButtonData>((b, i) => ({
            id: -(i + 1), // sentinela: backend gera ID real no save
            button_text: (b.title || '').trim() || 'Botão',
            button_type: 'url',
            url: b.url || '',
            button_order: i,
            position: i,
        }));

    if (nodeType === 'photo_template' || nodeType === 'photo') {
        return {
            ...base,
            title: template.title || '',
            subtitle: template.subtitle || '',
            media_url: template.image_url || base.media_url || '',
            media_type: 'image',
            image_aspect_ratio: 'horizontal',
            buttons,
        };
    }

    if (nodeType === 'message_template' || nodeType === 'message') {
        let message: string;
        if (isCard) {
            const parts = [template.title, template.subtitle].filter(Boolean) as string[];
            message = parts.join('\n\n');
        } else {
            message = template.text_content || template.title || '';
        }
        return {
            ...base,
            message,
            buttons,
        };
    }

    // Outros tipos (video, audio, etc) não recebem template no config principal —
    // o picker vai entrar via BlocksEditor (blocos adicionais).
    return base;
}

/** Quais nodeTypes suportam aplicar template no config principal. */
export function nodeTypeSupportsTemplate(nodeType: string): boolean {
    return [
        'photo_template', 'photo',
        'message_template', 'message',
    ].includes(nodeType);
}
